import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import MenuItem from '@mui/material/MenuItem'
import CircularProgress from '@mui/material/CircularProgress'
import update from 'immutability-helper'
import { round } from 'lodash'
import { useAPI } from 'contexts/APIProvider'
import { useAuthUser } from 'contexts/AuthUserProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import Paper from '@mui/material/Paper'
import FormField from 'components/FormField'
import CheckboxFormField from 'components/CheckboxFormField'
import ErrorList from 'components/ErrorList'
import SetupBillingAccount from 'components/SetupBillingAccount'
import AddressSearch from 'components/AddressSearch'
import LeadAvailabilityCheckFields from './LeadAvailabilityCheckFields'
import LeadActionPanelStepper from './LeadActionPanelStepper'
import LeadDetailContext from '../context'
import { LeadPracticeForm, LeadAvailabilityCheckForm, EstimateCost, EstimateCostForm } from 'types/interfaces'
import LeadPracticeFormError from 'errors/LeadPracticeFormError'
import LeadAvailabilityCheckFormError from 'errors/LeadAvailabilityCheckFormError'
import LeadMatchedJobFormError from 'errors/LeadMatchedJobFormError'
import Practice from 'logic/Practice'
import AvailabilityCheck from 'logic/AvailabilityCheck'
import { PAYMENT_OPTIONS_FOR_STANDALONE_PRACTICES, MARKETING_CHANNELS } from 'types/constants'
import { practiceJobsURL } from 'routes/urls'
import { filter, switchMap, take, timer, firstValueFrom } from 'rxjs'
import { formatCurrencyAmount } from 'logic/helpers'
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import DescriptionList from 'components/DescriptionList'

export type LeadActionStep =
  | 'create-practice'
  | 'set-password'
  | 'setup-billing'
  | 'confirm-booking'
  | 'looking-for-nurse'
  | 'add-availability-check'
  | 'availability-check-expired'

const STEP_INDEX: Record<LeadActionStep, number> = {
  'add-availability-check': 0,
  'create-practice': 1,
  'set-password': 2,
  'setup-billing': 3,
  'looking-for-nurse': 4,
  'confirm-booking': 5,
  'availability-check-expired': 6,
}


interface LeadActionPanelProps {
  refreshLead: () => Promise<void>
}

const LeadActionPanel: React.FC<LeadActionPanelProps> = ({ refreshLead }) => {
  const { authUser } = useAuthUser()
  const { lead, availabilityCheck } = React.useContext(LeadDetailContext)

  const step = React.useMemo<LeadActionStep>(() => {
    if (!lead || !availabilityCheck) return 'add-availability-check'
    if (availabilityCheck.is_expired) return 'availability-check-expired'
    if (lead.practice_id === null) return 'create-practice'
    if (authUser?.firebaseUser.isAnonymous) return 'set-password'
    if (lead.practice_is_stripe_ready === false) return 'setup-billing'
    if (availabilityCheck.isNurseFound || availabilityCheck.isBookingConfirmed) return 'confirm-booking'
    return 'looking-for-nurse'
  }, [lead, authUser, availabilityCheck])

  const numNurses = React.useMemo(() => {
    return availabilityCheck?.numTotal ?? 0
  }, [availabilityCheck?.numTotal])

  const stepIndex = STEP_INDEX[step]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <LeadActionPanelStepper currentStep={step} />

      {stepIndex >= 1 && stepIndex < 4 && (
        <Alert severity="info">
          { numNurses > 0 ? `Reaching out to ${numNurses} nurses nearby.` : 'Reaching out to nurses nearby...' } <br />
          Complete these steps while we search for your nurse. The sooner you're set up, the faster we can confirm your booking.
        </Alert>
      )}

      {step === 'create-practice' && <CreatePracticeStep refreshLead={refreshLead} />}
      {step === 'set-password' && <SetPasswordStep />}
      {step === 'setup-billing' && <SetupBillingStep refreshLead={refreshLead} />}
      {step === 'confirm-booking' && <ConfirmBookingStep refreshLead={refreshLead} />}
      {step === 'looking-for-nurse' && <LookingForNurseStep />}
      {step === 'add-availability-check' && <AddAvailabilityCheckStep />}
      {step === 'availability-check-expired' && <AvailabilityCheckExpiredStep />}
    </Box>
  )
}

// ─── Step: Create Practice ──────────────────────────────────────────────────

type CreatePracticeStepProps = {
  refreshLead: () => Promise<void>
}

const CreatePracticeStep: React.FC<CreatePracticeStepProps> = ({ refreshLead }) => {
  const { api } = useAPI()
  const { refreshUser } = useAuthUser()
  const { lead, availabilityCheck, gtmCheckStats } = React.useContext(LeadDetailContext)
  const sendDataToGTM = useGTMDispatch()

  const [form, setForm] = React.useState<LeadPracticeForm>({
    postcode: availabilityCheck?.postcode ?? '',
    latitude: availabilityCheck?.lat,
    longitude: availabilityCheck?.lng,
    country: 'United Kingdom',
  })
  const [addressTab, setAddressTab] = React.useState(0)
  const [error, setError] = React.useState<LeadPracticeFormError>()
  const [loading, setLoading] = React.useState(false)

  const updateForm = React.useCallback((name: string, value: any) => {
    setForm(prev => update(prev, { [name]: { $set: value } }))
    setError(prev => prev ? update(prev, { [name]: { $set: [] } }) : prev)
  }, [])

  const handleAddressSearchChange = React.useCallback((addressForm: LeadPracticeForm) => {
    setForm(prev => ({ ...prev, ...addressForm }))
    setError(prev => {
      if (!prev) return prev
      const cleared = { ...prev }
      for (const key of Object.keys(addressForm)) {
        (cleared as any)[key] = []
      }
      return cleared
    })
  }, [])

  const handleSubmit = React.useCallback(async () => {
    if (!lead) return
    setLoading(true)
    setError(undefined)

    try {
      const address = [form.line1, form.line2, form.city, form.postcode].filter(Boolean).join(', ')
      await api.createLeadPractice({ ...form, lead_id: lead.id, address })
      await refreshUser(true)
      sendDataToGTM({
        event: 'practice-created',
        num_checks_total: gtmCheckStats.numTotal,
        num_checks_with_nurse_found: gtmCheckStats.numNurseFound,
        pct_checks_with_nurse_found: gtmCheckStats.pctNurseFound,
      })
      await refreshLead()
    } catch (e: any) {
      if (e instanceof LeadPracticeFormError) {
        setError(e)
      } else {
        console.error('unhandled exception', e)
      }
    } finally {
      setLoading(false)
    }
  }, [lead, form, api, refreshUser, refreshLead, gtmCheckStats, sendDataToGTM])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">Your Practice Details</Typography>

      {error?.schema?.length ? (
        <Alert severity="error"><ErrorList errors={error.schema} /></Alert>
      ) : null}

      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={addressTab}
            onChange={(_, newValue: number) => setAddressTab(newValue)}
            aria-label="Address entry method"
          >
            <Tab label="Locate by postcode" id="address-tab-0" aria-controls="address-panel-0" />
            <Tab label="Enter manually" id="address-tab-1" aria-controls="address-panel-1" />
          </Tabs>
        </Box>
        {addressTab === 0 && (
          <Box role="tabpanel" id="address-panel-0" aria-labelledby="address-tab-0" sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <AddressSearch
              label="Practice Address"
              form={form}
              setForm={handleAddressSearchChange}
            />
          </Box>
        )}
        {addressTab === 1 && (
          <Box role="tabpanel" id="address-panel-1" aria-labelledby="address-tab-1" sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormField
              label="Address Line 1"
              value={form.line1 ?? ''}
              onChange={(e) => updateForm('line1', e.target.value)}
              errors={error?.line1}
            />
            <FormField
              label="Address Line 2"
              value={form.line2 ?? ''}
              onChange={(e) => updateForm('line2', e.target.value)}
              errors={error?.line2}
            />
            <FormField
              label="City"
              value={form.city ?? ''}
              onChange={(e) => updateForm('city', e.target.value)}
              errors={error?.city}
            />
            <FormField
              label="County"
              value={form.county ?? ''}
              onChange={(e) => updateForm('county', e.target.value)}
              errors={error?.county}
            />
            <FormField
              label="Country"
              value={form.country ?? ''}
              onChange={(e) => updateForm('country', e.target.value)}
              errors={error?.country}
            />
            <FormField
              label="Postcode"
              value={form.postcode ?? ''}
              onChange={(e) => updateForm('postcode', e.target.value)}
              errors={error?.postcode}
            />
          </Box>
        )}
      </Box>

      <FormField
        label="Affiliate Code (optional)"
        value={form.affiliate_code ?? ''}
        onChange={(e) => updateForm('affiliate_code', e.target.value)}
        errors={error?.affiliate_code}
        helperText="If you were referred by someone, enter their affiliate code here"
      />

      <FormField
        label="How did you hear about us?"
        select
        value={form.marketing_channel ?? ''}
        onChange={(e) => updateForm('marketing_channel', e.target.value)}
        errors={error?.marketing_channel}
      >
        <MenuItem value=''>Please select</MenuItem>
        {MARKETING_CHANNELS.map((channel) => (
          <MenuItem key={channel} value={channel}>{channel}</MenuItem>
        ))}
      </FormField>

      <CheckboxFormField
        label={
          <Typography variant='body2'>
            Check here to accept <a href='https://www.locumloop.com/agreements-practice' target='_blank' rel='noopener noreferrer'>the contract with locumloop</a>
          </Typography>
        }
        checked={form.agreed_contracts ?? false}
        onChange={(e) => updateForm('agreed_contracts', (e.target as HTMLInputElement).checked)}
        errors={error?.agreed_contracts}
      />

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            Setting up your practice...
          </Box>
        ) : 'Create Practice'}
      </Button>
    </Box>
  )
}

// ─── Step: Set Password ─────────────────────────────────────────────────────

const SetPasswordStep: React.FC = () => {
  const { linkCredentials } = useAuthUser()
  const { lead, gtmCheckStats } = React.useContext(LeadDetailContext)
  const sendDataToGTM = useGTMDispatch()

  const [password, setPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string>()

  const handleSubmit = React.useCallback(async () => {
    if (!lead) return
    if (password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    setLoading(true)
    setError(undefined)

    try {
      await linkCredentials(lead.email, password)
      sendDataToGTM({
        event: 'password-set',
        num_checks_total: gtmCheckStats.numTotal,
        num_checks_with_nurse_found: gtmCheckStats.numNurseFound,
        pct_checks_with_nurse_found: gtmCheckStats.pctNurseFound,
      })
    } catch (e: any) {
      setError(e.message ?? 'Failed to set password.')
    } finally {
      setLoading(false)
    }
  }, [lead, linkCredentials, password, confirmPassword, gtmCheckStats, sendDataToGTM])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">Set Your Password</Typography>
      <Typography variant="body2" color="text.secondary">
        Create a password to access your account. You'll use this to manage your bookings and view your nurses.
      </Typography>
      <Typography variant="body2">Account email: {lead?.email}</Typography>

      {error && <Alert severity="error">{error}</Alert>}

      <FormField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        helperText="Must be at least 6 characters"
      />
      <FormField
        label="Confirm Password"
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
      />

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            Setting password...
          </Box>
        ) : 'Set Password'}
      </Button>
    </Box>
  )
}

// ─── Step: Setup Billing ────────────────────────────────────────────────────

type SetupBillingStepProps = {
  refreshLead: () => Promise<void>
}

const SetupBillingStep: React.FC<SetupBillingStepProps> = ({ refreshLead }) => {
  const { api } = useAPI()
  const { showAlert } = useSnackBarAlert()
  const { lead, gtmCheckStats } = React.useContext(LeadDetailContext)
  const sendDataToGTM = useGTMDispatch()

  const [practice, setPractice] = React.useState<Practice>()
  const [loading, setLoading] = React.useState(true)

  const fetchPractice = React.useCallback(async () => {
    if (!lead?.practice_id) return
    setLoading(true)
    try {
      const practice = await api.getPractice(lead.practice_id)
      setPractice(practice)
    } catch (e: any) {
      showAlert('error', 'Failed to load practice')
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [api, lead?.practice_id, showAlert])

  const waitUntilPracticeIsReady = React.useCallback(async () => {
    if (!practice) return
    const waitUntilReady$ = timer(0, 3000).pipe(
      switchMap(() => api.getPractice(practice.practice_id)),
      filter(practice => practice.is_stripe_ready),
      take(1),
    )
    return firstValueFrom(waitUntilReady$)
  }, [api, practice])

  const onSuccess = React.useCallback(async () => {
    const checkStatsPayload = {
      num_checks_total: gtmCheckStats.numTotal,
      num_checks_with_nurse_found: gtmCheckStats.numNurseFound,
      pct_checks_with_nurse_found: gtmCheckStats.pctNurseFound,
    }
    setLoading(true)
    sendDataToGTM({ event: 'billing-info-submitted', ...checkStatsPayload })
    try {
      await waitUntilPracticeIsReady()
      sendDataToGTM({ event: 'billing-setup-confirmed', ...checkStatsPayload })
      await refreshLead()
    } catch (e: any) {
      showAlert('error', 'Failed to setup billing')
      console.error('unhandled exception', e)
    } finally {
      setLoading(false)
    }
  }, [waitUntilPracticeIsReady, refreshLead, showAlert, sendDataToGTM, gtmCheckStats])

  React.useEffect(() => {
    fetchPractice()
  }, [fetchPractice])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading billing setup...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">Setup Billing</Typography>
      <Typography variant="body2" color="text.secondary">
        Add a payment method to confirm nurse bookings. You'll only be charged when the job is completed.
      </Typography>
      <SetupBillingAccount
        practice={practice}
        onSuccess={onSuccess}
        availablePaymentOptions={PAYMENT_OPTIONS_FOR_STANDALONE_PRACTICES}
      />
    </Box>
  )
}

// ─── BookingSummary ─────────────────────────────────────────────────────────

type BookingSummaryProps = {
  availabilityCheck: AvailabilityCheck
}

const BookingSummary: React.FC<BookingSummaryProps> = ({ availabilityCheck }) => (
  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
    <DescriptionList
      labelWidth='20%'
      valueWidth='80%'
      data={[
        { label: 'Date', value: availabilityCheck.dateLabel() },
        { label: 'Time', value: availabilityCheck.timeLabel() },
        { label: 'Role', value: availabilityCheck.role },
        { label: 'Postcode', value: availabilityCheck.postcode },
        { label: 'Lunch Break', value: availabilityCheck.lunchBreakLabel },
        { label: 'Payment Schedule', value: availabilityCheck.paymentScheduleLabel },
      ]}
    />
  </Paper>
)

// ─── Step: Confirm Booking ──────────────────────────────────────────────────

type ConfirmBookingStepProps = {
  refreshLead: () => Promise<void>
}

const ConfirmBookingStep: React.FC<ConfirmBookingStepProps> = ({ refreshLead }) => {
  const { api } = useAPI()
  const { showAlert } = useSnackBarAlert()
  const { lead, availabilityCheck, paygradeStats, gtmCheckStats } = React.useContext(LeadDetailContext)
  const sendDataToGTM = useGTMDispatch()

  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<LeadMatchedJobFormError>()
  const [confirmed, setConfirmed] = React.useState(availabilityCheck?.isBookingConfirmed ?? false)

  const [estimatedCost, setEstimatedCost] = React.useState<EstimateCost>()

  const fetchEstimatedCost = React.useCallback(async () => {
    if (!availabilityCheck) return
    const tz = 'Europe/London'
    const specialistRequired = (
      availabilityCheck.sedation_required
      || availabilityCheck.radiography_required
      || availabilityCheck.implant_required
      || availabilityCheck.orthodontics_required
    )
    const form: EstimateCostForm = {
      practice_id: lead?.practice_id ?? undefined,
      date: availabilityCheck.start_at.setZone(tz).toFormat('yyyy-MM-dd'),
      start_time: availabilityCheck.start_at.setZone(tz).toFormat('HH:mm'),
      end_time: availabilityCheck.end_at.setZone(tz).toFormat('HH:mm'),
      lunch_break: availabilityCheck.lunch_break.as('minutes'),
      headcount: 1,
      role: availabilityCheck.role,
      specialist_required: specialistRequired,
      min_rate: paygradeStats?.min_rate,
      max_rate: paygradeStats?.max_rate,
      median_rate: paygradeStats?.median_rate,
    }
    try {
      const cost = await api.getEstimatedCost(form)
      setEstimatedCost(cost)
    } catch (e) {
      console.error('Failed to load estimated cost', e)
    }
  }, [api, lead?.practice_id, availabilityCheck, paygradeStats])

  React.useEffect(() => { fetchEstimatedCost() }, [fetchEstimatedCost])

  const handleConfirm = React.useCallback(async () => {
    if (!availabilityCheck) return
    setLoading(true)
    setError(undefined)

    try {
      await api.createLeadMatchedJob({ availability_check_id: availabilityCheck.id })
      sendDataToGTM({
        event: 'booking-confirmed',
        num_checks_total: gtmCheckStats.numTotal,
        num_checks_with_nurse_found: gtmCheckStats.numNurseFound,
        pct_checks_with_nurse_found: gtmCheckStats.pctNurseFound,
      })
      setConfirmed(true)
      await refreshLead()
      showAlert('success', 'Booking confirmed!')
    } catch (e: any) {
      if (e instanceof LeadMatchedJobFormError) {
        setError(e)
      } else {
        console.error('unhandled exception', e)
        showAlert('error', 'An unexpected error occurred.')
      }
    } finally {
      setLoading(false)
    }
  }, [api, availabilityCheck, showAlert, refreshLead, gtmCheckStats, sendDataToGTM])

  const isPendingAction = React.useMemo(() => {
    if (!lead) return true
    if (lead.num_checks_waiting_nurse > 0) return true
    if (lead.num_checks_waiting_confirmation > 0) return true
    return false
  }, [lead])

  if (confirmed) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="success" variant="filled">
          Thank you for confirming your booking! We're excited to have you on board. You'll receive a confirmation email with your nurse's details shortly. You can manage all your bookings from your dashboard.
        </Alert>
        {availabilityCheck && <BookingSummary availabilityCheck={availabilityCheck} />}
        {estimatedCost && (
          <Alert severity="info">
            <Typography>
              Estimated total price: <strong>{formatCurrencyAmount(estimatedCost.median_total, 'GBP')} per day</strong> ({round(estimatedCost.billable_hours, 1)} hours total)
            </Typography>
            {estimatedCost.discount ? (
              <Typography>{estimatedCost.discount}</Typography>
            ) : null}
            <Typography variant="caption">This is an estimate. Final charges may vary.</Typography>
          </Alert>
        )}
        {!isPendingAction && lead?.practice_id && (
          <Button
            variant="contained"
            href={practiceJobsURL(lead.practice_id)}
          >
            View Dashboard
          </Button>
        )}
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error?.schema?.length ? (
        <Alert severity="error"><ErrorList errors={error.schema} /></Alert>
      ) : null}

      <Alert severity="info" variant='filled'>
        A nurse has been matched to your booking! Please confirm promptly — nurses are in high demand and may become unavailable if not confirmed quickly.
      </Alert>

      {availabilityCheck && <BookingSummary availabilityCheck={availabilityCheck} />}

      {estimatedCost && (
        <Alert severity="info">
          <Typography>
            Expected total price: <strong>{formatCurrencyAmount(estimatedCost.median_total, 'GBP')} per day</strong> ({round(estimatedCost.billable_hours, 1)} hours total)
          </Typography>
          {estimatedCost.discount ? (
            <Typography>{estimatedCost.discount}</Typography>
          ) : null}
        </Alert>
      )}

      <Button
        variant="contained"
        onClick={handleConfirm}
        disabled={loading}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            Confirming...
          </Box>
        ) : 'Confirm Booking'}
      </Button>

    </Box>
  )
}

// ─── Step: Looking for Nurse ────────────────────────────────────────────────

const LookingForNurseStep: React.FC = () => {
  const { lead } = React.useContext(LeadDetailContext)
  const notifyText = lead?.phone
    ? `We'll notify you by email (${lead.email}) and phone (${lead.phone}) as soon as we find a match.`
    : `We'll notify you by email (${lead?.email}) as soon as we find a match.`

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="info">
        We're searching for the best available nurse near your practice. {notifyText}
      </Alert>
      <Typography variant="body2" color="text.secondary">
        This usually takes a few hours. You can safely close this page — we'll be in touch.
      </Typography>
    </Box>
  )
}

// ─── Step: Add Availability Check ───────────────────────────────────────────

const AddAvailabilityCheckStep: React.FC = () => {
  const { api } = useAPI()
  const { showAlert } = useSnackBarAlert()
  const { lead, availabilityCheck, setAvailabilityCheck } = React.useContext(LeadDetailContext)
  const sendDataToGTM = useGTMDispatch()

  const [form, setForm] = React.useState<LeadAvailabilityCheckForm>({})
  const [formError, setFormError] = React.useState<LeadAvailabilityCheckFormError>()
  const [loading, setLoading] = React.useState(false)

  const updateForm = React.useCallback((name: string, value: any) => {
    setForm(prev => update(prev, { [name]: { $set: value } }))
    setFormError(prev => prev ? update(prev, { [name]: { $set: [] } }) : prev)
  }, [])

  const handleSubmit = React.useCallback(async () => {
    if (!lead) return
    setLoading(true)
    setFormError(undefined)

    try {
      const checkId = await api.createLeadAvailabilityCheck({
        ...form,
        lead_id: lead.id,
        lat: availabilityCheck?.lat,
        lng: availabilityCheck?.lng,
      })
      const check = await api.getAvailabilityCheck(checkId)
      setAvailabilityCheck(check)
      sendDataToGTM({ event: 'availability-check-added' })
      setForm({})
    } catch (e: any) {
      if (e instanceof LeadAvailabilityCheckFormError) {
        setFormError(e)
      } else {
        console.error('unhandled exception', e)
        showAlert('error', 'An unexpected error occurred.')
      }
    } finally {
      setLoading(false)
    }
  }, [lead, form, api, showAlert, availabilityCheck, setAvailabilityCheck, sendDataToGTM])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">When do you need a nurse?</Typography>
      <Typography variant="body2">
        Tell us when you need a nurse and we will find the best match for you.
      </Typography>

      {formError?.schema?.length ? (
        <Alert severity="error"><ErrorList errors={formError.schema} /></Alert>
      ) : null}

      <LeadAvailabilityCheckFields
        form={form}
        error={formError}
        updateForm={updateForm}
      />

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            Finding nurses...
          </Box>
        ) : 'Find a Nurse'}
      </Button>
    </Box>
  )
}

// ─── Step: Availability Check Expired ─────────────────────────────────────────

const AvailabilityCheckExpiredStep: React.FC = () => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="info" variant='filled'>The job date has passed. Please add another date.</Alert>
    </Box>
  )
}

export default LeadActionPanel
